import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileDown, FileText, ChevronRight, Building2 } from "lucide-react";

/**
 * Dealer Performance / Dealer-wise Complaint Report.
 * - Sales Team users (and admin/service_head/manager) can view + export.
 * - Click a row to drill down into product/model breakdown and the complaint list.
 */
export default function DealerPerformanceReport() {
  const [summaries, setSummaries] = useState([]);
  const [totalScope, setTotalScope] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillData, setDrillData] = useState(null);
  const [filters, setFilters] = useState({
    dealer: "", brand: "", product_category: "", product_name: "", model_number: "",
    warranty_status: "", status: "", province: "", district: "", area: "",
    date_from: "", date_to: "",
  });

  const params = useMemo(() => {
    const out = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) out[k] = v; });
    return out;
  }, [filters]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/dealer-performance", { params });
      setSummaries(data.summaries || []);
      setTotalScope(data.total_complaints_in_scope || 0);
    } catch (e) {
      toast.error("Failed to load report", { description: formatApiErrorDetail(e.response?.data?.detail) });
      setSummaries([]); setTotalScope(0);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const update = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const exportFile = (fmt) => {
    const qs = new URLSearchParams({ fmt, ...params }).toString();
    downloadBlob({
      url: `/reports/dealer-performance?${qs}`,
      filename: `dealer_performance.${fmt}`,
    });
  };

  const openDrill = async (dealerName) => {
    setDrillOpen(true);
    setDrillData(null);
    try {
      const { data } = await api.get(
        `/reports/dealer-performance/${encodeURIComponent(dealerName)}`,
        { params },
      );
      setDrillData(data);
    } catch (e) {
      toast.error("Failed to load dealer detail", { description: formatApiErrorDetail(e.response?.data?.detail) });
      setDrillOpen(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="dealer-performance-page">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Dealer Performance Report</h1>
          <p className="text-sm text-slate-500 mt-1">
            Complaint volume, status, products, and ageing — grouped by dealer. Click any row to drill down.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => exportFile("xlsx")} data-testid="dp-export-xlsx">
            <FileDown size={14} className="mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportFile("pdf")} data-testid="dp-export-pdf">
            <FileText size={14} className="mr-1" /> PDF
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            <Fld label="Dealer"><Input value={filters.dealer} onChange={(e) => update("dealer", e.target.value)} data-testid="dp-f-dealer" placeholder="exact name" /></Fld>
            <Fld label="Brand"><Input value={filters.brand} onChange={(e) => update("brand", e.target.value)} data-testid="dp-f-brand" /></Fld>
            <Fld label="Category"><Input value={filters.product_category} onChange={(e) => update("product_category", e.target.value)} data-testid="dp-f-category" /></Fld>
            <Fld label="Product Name"><Input value={filters.product_name} onChange={(e) => update("product_name", e.target.value)} data-testid="dp-f-product" /></Fld>
            <Fld label="Model Number"><Input value={filters.model_number} onChange={(e) => update("model_number", e.target.value)} data-testid="dp-f-model" /></Fld>
            <Fld label="Warranty"><Input value={filters.warranty_status} onChange={(e) => update("warranty_status", e.target.value)} data-testid="dp-f-warranty" /></Fld>
            <Fld label="Status"><Input value={filters.status} onChange={(e) => update("status", e.target.value)} data-testid="dp-f-status" /></Fld>
            <Fld label="Province"><Input value={filters.province} onChange={(e) => update("province", e.target.value)} data-testid="dp-f-province" /></Fld>
            <Fld label="District"><Input value={filters.district} onChange={(e) => update("district", e.target.value)} data-testid="dp-f-district" /></Fld>
            <Fld label="Area"><Input value={filters.area} onChange={(e) => update("area", e.target.value)} data-testid="dp-f-area" /></Fld>
            <Fld label="Date From"><Input type="date" value={filters.date_from} onChange={(e) => update("date_from", e.target.value)} data-testid="dp-f-date-from" /></Fld>
            <Fld label="Date To"><Input type="date" value={filters.date_to} onChange={(e) => update("date_to", e.target.value)} data-testid="dp-f-date-to" /></Fld>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="text-xs text-slate-500">{summaries.length} dealers · {totalScope} complaints in scope</div>
            <Button onClick={load} className="bg-slate-900 text-white" data-testid="dp-apply">Apply Filters</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <Th>Dealer</Th>
                  <Th align="right">Total</Th>
                  <Th align="right">Open</Th>
                  <Th align="right">Closed</Th>
                  <Th align="right">Pending</Th>
                  <Th align="right">Reopened</Th>
                  <Th align="right">Duplicate</Th>
                  <Th align="right">Avg Close (days)</Th>
                  <Th align="right">Avg 1st Visit (days)</Th>
                  <Th align="right">CSAT</Th>
                  <Th>Last Complaint</Th>
                  <Th align="right">Action</Th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={12} className="text-center py-12 text-slate-400">Loading…</td></tr>}
                {!loading && summaries.length === 0 && (
                  <tr><td colSpan={12} className="text-center py-12 text-slate-400">No dealer data for current filters.</td></tr>
                )}
                {summaries.map((s) => (
                  <tr key={s.dealer_name} className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => openDrill(s.dealer_name)}
                      data-testid={`dp-row-${s.dealer_name.replace(/\s+/g, "_")}`}>
                    <td className="font-medium flex items-center gap-2 py-2 px-2"><Building2 size={14} className="text-slate-400" />{s.dealer_name}</td>
                    <td className="text-right font-mono font-semibold">{s.total_complaints}</td>
                    <td className="text-right font-mono">{s.open_complaints}</td>
                    <td className="text-right font-mono">{s.closed_complaints}</td>
                    <td className="text-right font-mono">{s.pending_complaints}</td>
                    <td className="text-right font-mono">{s.reopened_complaints}</td>
                    <td className="text-right font-mono">{s.duplicate_complaints}</td>
                    <td className="text-right font-mono">{s.avg_closure_days ?? "—"}</td>
                    <td className="text-right font-mono">{s.avg_first_visit_days ?? "—"}</td>
                    <td className="text-right font-mono">{s.csat_average ?? "—"}</td>
                    <td className="text-xs">{(s.last_complaint_date || "").slice(0, 10) || "—"}</td>
                    <td className="text-right"><Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); openDrill(s.dealer_name); }} data-testid={`dp-drill-${s.dealer_name.replace(/\s+/g, "_")}`}>Detail <ChevronRight size={12} /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <DrillDialog open={drillOpen} onClose={() => { setDrillOpen(false); setDrillData(null); }} data={drillData} />
    </div>
  );
}

function DrillDialog({ open, onClose, data }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="dp-drill-dialog">
        <DialogHeader>
          <DialogTitle>{data ? data.dealer_name : "Loading…"}</DialogTitle>
          <DialogDescription>Product / model breakdown and complaint list for this dealer.</DialogDescription>
        </DialogHeader>
        {!data && <div className="py-8 text-center text-slate-400">Loading dealer detail…</div>}
        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
              <Stat label="Total" value={data.summary.total_complaints} />
              <Stat label="Open" value={data.summary.open_complaints} />
              <Stat label="Closed" value={data.summary.closed_complaints} />
              <Stat label="Reopened" value={data.summary.reopened_complaints} />
              <Stat label="Duplicate" value={data.summary.duplicate_complaints} />
              <Stat label="Avg Close (days)" value={data.summary.avg_closure_days ?? "—"} />
              <Stat label="Avg 1st Visit (days)" value={data.summary.avg_first_visit_days ?? "—"} />
              <Stat label="CSAT" value={data.summary.csat_average ?? "—"} />
              <Stat label="Last Complaint" value={(data.summary.last_complaint_date || "").slice(0, 10) || "—"} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <BreakdownCard title="By Brand" data={data.summary.by_brand} testid="bd-brand" />
              <BreakdownCard title="By Product Category" data={data.summary.by_product_category} testid="bd-category" />
              <BreakdownCard title="By Product Name" data={data.summary.by_product_name} testid="bd-product" />
              <BreakdownCard title="By Model Number" data={data.summary.by_model_number} testid="bd-model" />
              <BreakdownCard title="By Warranty Status" data={data.summary.by_warranty_status} testid="bd-warranty" />
              <BreakdownCard title="By Service Type" data={data.summary.by_service_type} testid="bd-service" />
            </div>
            <Card>
              <CardContent className="p-0">
                <div className="px-3 py-2 text-xs text-slate-500">{data.count} complaints</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b"><tr>
                      <Th>Complaint #</Th><Th>Date</Th><Th>Status</Th><Th>Brand</Th><Th>Product</Th><Th>Model</Th>
                      <Th>Customer</Th><Th>Technician</Th><Th>Closed On</Th><Th>CSAT</Th>
                    </tr></thead>
                    <tbody>
                      {(data.complaints || []).map((c) => (
                        <tr key={c.id} data-testid={`dp-detail-${c.complaint_number}`}>
                          <td><Link to={`/complaints/${c.id}`} className="font-mono hover:underline">{c.complaint_number}</Link></td>
                          <td className="text-xs">{(c.registered_date || "").slice(0, 10)}</td>
                          <td className="text-xs">{c.status}</td>
                          <td>{c.brand}</td>
                          <td>{c.product_name}</td>
                          <td className="font-mono text-xs">{c.model_number}</td>
                          <td>{c.customer_name}</td>
                          <td>{c.technician_name || "—"}</td>
                          <td className="text-xs">{(c.closing_date || "").slice(0, 10) || "—"}</td>
                          <td className="text-right font-mono">{c.csat_rating ?? "—"}</td>
                        </tr>
                      ))}
                      {(data.complaints || []).length === 0 && <tr><td colSpan={10} className="py-8 text-center text-slate-400">No complaints.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-md p-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{label}</div>
      <div className="text-base font-mono font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function BreakdownCard({ title, data, testid }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  return (
    <Card><CardContent className="p-3" data-testid={testid}>
      <div className="font-semibold text-sm mb-2 text-slate-700">{title}</div>
      {entries.length === 0 && <div className="text-xs text-slate-400">—</div>}
      <div className="space-y-1">
        {entries.slice(0, 12).map(([k, v]) => (
          <div key={k} className="flex justify-between text-xs">
            <span className="text-slate-700 truncate pr-2">{k}</span>
            <span className="font-mono font-semibold text-slate-900">{v}</span>
          </div>
        ))}
        {entries.length > 12 && <div className="text-xs text-slate-400">+{entries.length - 12} more…</div>}
      </div>
    </CardContent></Card>
  );
}

function Fld({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
function Th({ children, align }) {
  return <th className={`${align === "right" ? "text-right" : "text-left"} text-xs uppercase tracking-wide text-slate-600 font-semibold px-2 py-2`}>{children}</th>;
}
